import React from 'react';
import { mediaService, type MediaAsset } from '../../services/studio.service';
import { FONT_MONO } from '../../views/landing/landingData';
import { fmtBytes } from '../../views/landing/format';
import { BrandBtn, EmptyCard, Eyebrow, Icons, RoundBtn, Spinner, Tip } from './studioUi';
import { useConfirm } from '../common/ConfirmDialog';
import { useToast } from '../common/Toast';

interface MediaPanelProps {
    /** Only recorded on the asset so the library can later be filtered per app. */
    applicationId?: string;
}

/**
 * Media tab: the user's asset library, in the studio design's tokens.
 *
 * Assets are per-USER, so the same logo is available in every app. Each one has a
 * short `assetId` the user pastes into a prompt ("use img_7f3a2b for the hero") —
 * core-api resolves referenced ids into real URLs and hands them to the agent, so
 * the generated app uses the actual file.
 */
export const MediaPanel: React.FC<MediaPanelProps> = ({ applicationId }) => {
    const [assets, setAssets] = React.useState<MediaAsset[]>([]);
    const [used, setUsed] = React.useState(0);
    const [quota, setQuota] = React.useState(0);
    const [enabled, setEnabled] = React.useState(true);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [uploading, setUploading] = React.useState<{ name: string; pct: number } | null>(null);
    const [copied, setCopied] = React.useState<string | null>(null);
    const [dragging, setDragging] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const confirm = useConfirm();
    const toast = useToast();

    const load = React.useCallback(async () => {
        setLoading(true);
        const res = await mediaService.list();
        if (res.success && res.data) {
            setAssets(res.data.assets);
            setUsed(res.data.usedBytes);
            setQuota(res.data.quotaBytes);
            setEnabled(res.data.enabled);
            setError(null);
        } else {
            setError((res as any).message || 'Could not load your media.');
        }
        setLoading(false);
    }, []);

    React.useEffect(() => {
        void load();
    }, [load]);

    const uploadFiles = async (files: FileList | File[]) => {
        setError(null);
        for (const file of Array.from(files)) {
            setUploading({ name: file.name, pct: 0 });
            const res = await mediaService.upload(file, {
                applicationId,
                onProgress: (f) => setUploading({ name: file.name, pct: Math.round(f * 100) }),
            });
            setUploading(null);
            if (res.success && res.data) {
                setAssets((prev) => [res.data as MediaAsset, ...prev]);
                setUsed((prev) => prev + (res.data as MediaAsset).bytes);
            } else {
                setError((res as any).message || `Could not upload ${file.name}.`);
                break; // a quota/type error will repeat for every remaining file
            }
        }
    };

    const copyId = async (assetId: string) => {
        try {
            await navigator.clipboard.writeText(assetId);
            setCopied(assetId);
            setTimeout(() => setCopied(null), 1500);
        } catch {
            setError('Could not copy — copy it manually: ' + assetId);
        }
    };

    const remove = async (assetId: string) => {
        const asset = assets.find((a) => a.assetId === assetId);
        const ok = await confirm({
            title: <>Delete <em>{asset?.originalName || assetId}</em>?</>,
            description: `Sites that already use this file will lose it, and prompts that mention ${assetId} will no longer resolve.`,
            confirmLabel: 'Delete file',
            onConfirm: async () => {
                const res = await mediaService.remove(assetId);
                if (!res.success) throw new Error((res as any).message || 'Could not delete the file.');
            },
        });
        if (!ok) return;
        setAssets((prev) => {
            const gone = prev.find((a) => a.assetId === assetId);
            if (gone) setUsed((u) => Math.max(0, u - gone.bytes));
            return prev.filter((a) => a.assetId !== assetId);
        });
        toast.success(`${asset?.originalName || assetId} deleted.`);
    };

    const openPicker = () => inputRef.current?.click();

    return (
        <div style={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'auto', padding: 22, display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: '100%', maxWidth: 980, display: 'flex', flexDirection: 'column', gap: 16, alignSelf: 'flex-start' }}>
                {/* Header + quota */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 220 }}>
                        <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--hv-ink)' }}>Media library</div>
                        <p style={{ margin: '4px 0 0', fontSize: 12.5, lineHeight: 1.6, color: 'var(--hv-body)' }}>
                            Upload images and videos, then mention an <span style={{ fontFamily: FONT_MONO, fontSize: 11.5, color: 'var(--hv-mint-ink)' }}>asset id</span> in chat to use it in your site.
                        </p>
                    </div>
                    {quota > 0 && <Eyebrow>{fmtBytes(used)} / {fmtBytes(quota)}</Eyebrow>}
                    <Tip label="Refresh">
                        <RoundBtn size={34} filled onClick={() => void load()} aria-label="Refresh media">
                            <span style={{ display: 'flex', animation: loading ? 'hvSpin 0.8s linear infinite' : undefined }}>{Icons.reload({})}</span>
                        </RoundBtn>
                    </Tip>
                    {enabled && (
                        <BrandBtn onClick={openPicker} disabled={!!uploading} style={{ height: 38, padding: '0 16px', fontSize: 13 }}>
                            {Icons.upload({ size: 15 })} {uploading ? 'Uploading…' : 'Upload file'}
                        </BrandBtn>
                    )}
                    <input
                        ref={inputRef}
                        type="file"
                        multiple
                        accept="image/*,.heic,.heif,video/mp4,video/webm,video/quicktime,.mov,.m4v"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                            if (e.target.files?.length) void uploadFiles(e.target.files);
                            e.target.value = '';
                        }}
                    />
                </div>

                {!enabled && !loading ? (
                    <EmptyCard icon={Icons.media({ size: 23, sw: 1.8 })} title="Media library is off">
                        Object storage is not configured on the server yet (MEDIA_S3_ENDPOINT / MEDIA_PUBLIC_URL). Uploads will work once it is.
                    </EmptyCard>
                ) : (
                    <>
                        {/* Dropzone */}
                        <div
                            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragging(false);
                                if (e.dataTransfer.files?.length) void uploadFiles(e.dataTransfer.files);
                            }}
                            onClick={openPicker}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openPicker()}
                            className="hv-h-brand-bd"
                            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '26px 20px', borderRadius: 16, textAlign: 'center', cursor: 'pointer', border: `1.5px dashed ${dragging ? 'var(--brand,#13422C)' : 'var(--hv-line-2)'}`, background: dragging ? 'var(--hv-mint)' : 'var(--hv-card)', transition: 'border-color 0.18s ease, background 0.18s ease' }}
                        >
                            {uploading ? (
                                <>
                                    <Spinner size={20} />
                                    <span style={{ fontSize: 12.5, color: 'var(--hv-body)', maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{uploading.name}</span>
                                    <span style={{ width: 200, height: 4, borderRadius: 999, background: 'var(--hv-line)', overflow: 'hidden' }}>
                                        <span style={{ display: 'block', height: '100%', width: `${uploading.pct}%`, background: 'var(--brand,#13422C)', transition: 'width 0.2s ease' }} />
                                    </span>
                                </>
                            ) : (
                                <>
                                    <span style={{ color: 'var(--hv-mint-ink)', display: 'flex' }}>{Icons.upload({})}</span>
                                    <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--hv-ink)' }}>Drop files here, or click to choose</span>
                                    <Eyebrow style={{ letterSpacing: '0.08em', fontSize: 9.5 }}>IMAGES: JPG, PNG, WEBP, GIF, AVIF, SVG · VIDEO: MP4, WEBM, MOV</Eyebrow>
                                </>
                            )}
                        </div>
                        {error && (
                            <p style={{ margin: 0, fontSize: 12.5, fontWeight: 600, color: 'var(--hv-danger)' }}>{error}</p>
                        )}

                        {/* Grid */}
                        {loading && assets.length === 0 ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '40px 0' }}>
                                <Spinner size={14} /><Eyebrow>LOADING MEDIA…</Eyebrow>
                            </div>
                        ) : assets.length === 0 ? (
                            <EmptyCard icon={Icons.media({ size: 23, sw: 1.8 })} title="No media yet">
                                Ready to get started? Click <span style={{ fontWeight: 700, color: 'var(--hv-ink)' }}>Upload file</span> above and your assets will appear right here.
                            </EmptyCard>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 }}>
                                {assets.map((a) => (
                                    <div key={a.assetId} className="hv-media-card hv-h-brand-bd" style={{ borderRadius: 12, overflow: 'hidden', background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)', transition: 'border-color 0.25s ease', display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ position: 'relative', aspectRatio: '4 / 3', background: 'var(--hv-card-2)', overflow: 'hidden' }}>
                                            {a.kind === 'image' ? (
                                                <img src={a.url} alt={a.alt || a.originalName} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                            ) : (
                                                <video src={a.url} preload="metadata" muted style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                            )}
                                            <span style={{ position: 'absolute', top: 8, left: 8, display: 'flex', alignItems: 'center', height: 21, padding: '0 8px', borderRadius: 999, background: 'rgba(20,21,22,0.7)', color: '#F3F2EC', fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '0.12em' }}>
                                                {a.kind.toUpperCase()}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => void remove(a.assetId)}
                                                aria-label="Delete file"
                                                title="Delete"
                                                className="hv-media-del"
                                                style={{ position: 'absolute', top: 6, right: 6, width: 28, height: 28, borderRadius: 999, border: 0, background: 'rgba(20,21,22,0.7)', color: '#F3F2EC', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                            >
                                                {Icons.trash({})}
                                            </button>
                                        </div>
                                        <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 5 }}>
                                            <button
                                                type="button"
                                                onClick={() => void copyId(a.assetId)}
                                                title="Copy the asset id to use it in a prompt"
                                                className="hv-h-mint"
                                                style={{ display: 'flex', alignItems: 'center', gap: 7, width: '100%', height: 28, padding: '0 9px', border: 0, borderRadius: 999, background: 'var(--hv-card)', color: 'var(--hv-mint-ink)', fontFamily: FONT_MONO, fontSize: 11, cursor: 'pointer', textAlign: 'left' }}
                                            >
                                                <span style={{ flex: 'none', display: 'flex', color: copied === a.assetId ? 'var(--hv-leaf)' : 'inherit' }}>{copied === a.assetId ? Icons.check({ size: 12, sw: 2.8 }) : Icons.copy({ size: 12 })}</span>
                                                <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{copied === a.assetId ? 'Copied' : a.assetId}</span>
                                            </button>
                                            <div title={a.originalName} style={{ fontSize: 12, fontWeight: 600, color: 'var(--hv-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.originalName}</div>
                                            <div style={{ fontSize: 11.5, color: 'var(--hv-muted)' }}>
                                                {fmtBytes(a.bytes)}{a.width && a.height ? ` · ${a.width}×${a.height}` : ''}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
